'use client';

import React, { useState, Suspense, useEffect, useCallback } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';

import DialogContentText from '@mui/material/DialogContentText';
import FormHelperText from '@mui/material/FormHelperText';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import CircularProgress from '@mui/material/CircularProgress';

import styles from '@/styles/pages/login.module.scss';

import { useMutation, useQuery } from '@tanstack/react-query';
import { apiClient } from '@core/api';
import {
  getApplicationConfiguration,
  isMaintenanceModeBypassedForUser,
  isMaintenanceModeEnabled,
} from '@core/applicationConfiguration';
import { useToast } from '@providers/ToastProvider';
import type { Contact } from '@/src/utils/types';
import { useSetUser, useUser } from '../../_providers/UserProvider';
import { LoginLayout } from './shared';

type LoginErrorBody = {
  message?: string;
};

type LockoutResponse = {
  status?: boolean;
};

const LoginScreen = () => {
  const user = useUser();
  const isUserAuthenticated = user !== null;
  const setUser = useSetUser();
  const toast = useToast();
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirect = searchParams?.get('redirect');

  const [inputError, setInputError] = useState<Record<string, string | boolean>>({
    email: false,
    password: false,
  });
  const [responseError, setResponseError] = useState<string | boolean>(false);
  const [showResetPasswordForm, setShowResetPasswordForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const maintenanceModeQuery = useQuery({
    queryKey: ['application-configuration', 'maintenance-mode'],
    queryFn: getApplicationConfiguration,
  });

  const maintenanceModeEnabled = isMaintenanceModeEnabled(maintenanceModeQuery.data);
  const maintenanceModeAppliesToUser = maintenanceModeEnabled && !isMaintenanceModeBypassedForUser(user);

  const redirectUser = useCallback(() => {
    // If a redirect is present, send us there. Otherwise take us to the shipments page.
    if (redirect) {
      router.push(redirect);
    } else {
      router.push('/shipments');
    }
  }, [redirect, router]);

  useEffect(() => {
    if (isUserAuthenticated && !maintenanceModeAppliesToUser) {
      redirectUser();
    }
  }, [isUserAuthenticated, maintenanceModeAppliesToUser, redirectUser]);

  useEffect(() => {
    if (!maintenanceModeQuery.isLoading && maintenanceModeAppliesToUser) {
      router.replace('/maintenance');
    }
  }, [maintenanceModeAppliesToUser, maintenanceModeQuery.isLoading, router]);

  const authMutation = useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      apiClient('auth').post({ email, password }),
  });

  const lockoutCheckMutation = useMutation({
    mutationFn: ({ email }: { email: string }) => apiClient('isUserLockedOut').post({ email }),
  });

  const resetPasswordMutation = useMutation({
    mutationFn: ({ email }: { email: string }) => apiClient('resetPassword').post({ email }),
  });

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    const newInputError = { ...inputError };
    newInputError.email = newValue === '' ? 'Please enter a username or email address.' : false;

    setEmail(newValue);
    setInputError(newInputError);
  };

  const handleRecoveryEmailChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRecoveryEmail(e.target.value);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    const newInputError = { ...inputError };
    newInputError.password = newValue === '' ? 'Please enter a valid password.' : false;

    setPassword(newValue);
    setInputError(newInputError);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const emailValue = email || '';
    const passwordValue = password || '';

    if (emailValue.trim() === '' || passwordValue.trim() === '') {
      return;
    }

    setLoading(true);

    authMutation.mutate(
      {
        email: emailValue,
        password: passwordValue,
      },
      {
        onSuccess: (data: Contact) => {
          setLoading(false);
          setResponseError(false);
          setUser(data);
        },
        onError: (err) => {
          const errorResponse = err as { status?: number; body?: LoginErrorBody };
          console.error('Response:', errorResponse);

          let errorMessage = 'An error occurred. Please try again.';

          try {
            if (errorResponse.body?.message) {
              errorMessage = errorResponse.body.message;
            } else if (errorResponse.status) {
              errorMessage = `Error: ${errorResponse.status}`;
            } else {
              errorMessage = JSON.stringify(errorResponse.body);
            }
          } catch (e) {
            console.error('Error parsing JSON response:', e);
          }

          setLoading(false);
          setPassword('');
          setResponseError(errorMessage);
          setInputError({
            email: false,
            password: ' ',
          });
        },
      }
    );
  };

  const handleResetPassword = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();

    if (!recoveryEmail || !recoveryEmail.trim()) {
      toast?.show('Please enter an email address.', 4000);
      return;
    }

    lockoutCheckMutation.mutate(
      { email: recoveryEmail },
      {
        onSuccess: (response: LockoutResponse) => {
          // If the user is locked out, cancel this reset.
          if (response.status === true) {
            toast?.show('This user has been locked out and cannot be reset.', 10000);
            return;
          }

          // The user is NOT locked out. Continue with the reset.
          resetPasswordMutation.mutate(
            { email: recoveryEmail },
            {
              onSuccess: () => {
                toast?.show('An email has been sent to the account associated with that email address.', 10000);
                handleCloseResetDialogue();
              },
            }
          );
        },
      }
    );
  };

  const handleShowResetDialogue = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowResetPasswordForm(true);
  };

  const handleCloseResetDialogue = () => {
    setShowResetPasswordForm(false);
  };

  const submitClasses = ['login-submit', 't-login-button-submit'];
  const submitDisabled = loading || !email || !password;

  if (isUserAuthenticated) {
    return <></>;
  }

  if (maintenanceModeAppliesToUser) {
    return (
      <LoginLayout>
        <div className={styles['login-status-content']}>
          <CircularProgress size={36} />
        </div>
      </LoginLayout>
    );
  }

  return (
    <>
      <LoginLayout>
        {maintenanceModeQuery.isLoading ? (
          <div className={styles['login-status-content']}>
            <CircularProgress size={36} />
          </div>
        ) : (
          <form className={[styles['login-form'], 'login-form', 't-login-form'].join(' ')} onSubmit={handleSubmit}>
            <TextField
              name="email"
              label="Email"
              className={['login-form-email-input', 't-login-form-email-input'].join(' ')}
              margin="dense"
              helperText={inputError.email || 'Email addresses are not case sensitive.'}
              onChange={handleEmailChange}
              value={email}
              error={!!inputError.email}
              disabled={loading}
              autoFocus
              fullWidth
              required
            />

            <TextField
              name="password"
              label="Password"
              type="password"
              margin="dense"
              className={['login-form-password-input', 't-login-form-password-input'].join(' ')}
              helperText={inputError.password || ''}
              onChange={handlePasswordChange}
              value={password}
              error={!!inputError.password}
              disabled={loading}
              fullWidth
              required
            />

            <Button
              type="submit"
              variant="contained"
              disabled={submitDisabled}
              fullWidth={true}
              className={submitClasses.join(' ')}
            >
              Log in to evo information system
            </Button>

            <Button
              className={[
                styles['login-forgot-password'],
                'login-link-reset-password',
                't-login-link-reset-password',
              ].join(' ')}
              size="small"
              onClick={handleShowResetDialogue}
            >
              I forgot my login or password
            </Button>
          </form>
        )}

        {responseError && !maintenanceModeQuery.isLoading && (
          <div
            className={[styles['login-form-error'], 'login-link-reset-password', 't-login-link-reset-password'].join(
              ' '
            )}
          >
            <FormHelperText error>{responseError}</FormHelperText>
          </div>
        )}
      </LoginLayout>

      <Dialog open={showResetPasswordForm} onClose={handleCloseResetDialogue}>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please enter an email address for the account you wish to recover. Passwords are{' '}
            <strong>instantly reset</strong>, immediately <strong>upon submission</strong>!
          </DialogContentText>
          <br />
          <TextField
            label="Email Address"
            type="email"
            value={recoveryEmail}
            onChange={handleRecoveryEmailChange}
            autoFocus={true}
            fullWidth={true}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResetDialogue} color="primary">
            Cancel
          </Button>
          <Button onClick={handleResetPassword} color="primary">
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Use of SearchParams requires this
const SuspensedLogin = () => (
  <Suspense>
    <LoginScreen />
  </Suspense>
);

export default SuspensedLogin;
